import { Head, Link } from '@inertiajs/react'
import SuperAdminLayout from './_SuperAdminLayout'
import EmailHealthBanner, { EmailHealth } from './_EmailHealthBanner'

interface Stats {
  tenant_count: number
  active_tenant_count: number
  suspended_tenant_count: number
  pending_count: number
  user_count: number
  team_count: number
  backup_count: number
}

interface PendingSummary {
  id: number
  proposed_org_name: string
  contact_email: string
  contact_full_name: string
  requested_at_display: string
}

interface TenantSummary {
  id: number
  name: string
  slug: string
  status: string
  user_count: number
  team_count: number
  last_backup_display: string
  created_at_display: string
}

interface Props {
  stats: Stats
  email_health: EmailHealth
  recent_pending: PendingSummary[]
  recent_tenants: TenantSummary[]
}

export default function SuperAdminDashboard({ stats, email_health, recent_pending, recent_tenants }: Props) {
  return (
    <SuperAdminLayout>
      <Head title="SuperAdmin · Dashboard" />
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-text-primary">Dashboard</h1>
        <div className="flex gap-2">
          <Link href="/super_admin/tenants/new" className="px-4 py-2 bg-[#ED1C24] text-white rounded-lg text-sm font-semibold hover:bg-[#C7161E]">
            + Create tenant
          </Link>
        </div>
      </div>

      <EmailHealthBanner health={email_health} className="mb-6" />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <Stat label="Tenants" value={stats.tenant_count} hint={`${stats.active_tenant_count} active · ${stats.suspended_tenant_count} suspended`} />
        <Stat label="Pending signups" value={stats.pending_count} hint="Awaiting review" />
        <Stat label="Users" value={stats.user_count} hint={`${stats.team_count} teams`} />
        <Stat label="Backups stored" value={stats.backup_count} hint="Across all tenants" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Section title="Recent pending signups" link="/super_admin/pending_tenants" linkLabel="View all">
          {recent_pending.length === 0 ? (
            <Empty text="No pending tenants." />
          ) : (
            <ul className="divide-y divide-stone/10">
              {recent_pending.map(p => (
                <li key={p.id} className="py-3 flex items-center justify-between">
                  <div>
                    <Link href={`/super_admin/pending_tenants/${p.id}`} className="font-semibold text-text-primary hover:underline">
                      {p.proposed_org_name}
                    </Link>
                    <div className="text-xs text-text-muted">{p.contact_full_name} · {p.contact_email}</div>
                  </div>
                  <span className="text-xs text-text-muted">{p.requested_at_display}</span>
                </li>
              ))}
            </ul>
          )}
        </Section>

        <Section title="Recent tenants" link="/super_admin/tenants" linkLabel="View all">
          {recent_tenants.length === 0 ? (
            <Empty text="No tenants yet." />
          ) : (
            <ul className="divide-y divide-stone/10">
              {recent_tenants.map(t => (
                <li key={t.id} className="py-3 flex items-center justify-between">
                  <div>
                    <Link href={`/super_admin/tenants/${t.id}`} className="font-semibold text-text-primary hover:underline">
                      {t.name}
                    </Link>
                    <div className="text-xs text-text-muted">
                      {t.user_count} users · {t.team_count} teams · last backup {t.last_backup_display}
                    </div>
                  </div>
                  <StatusPill status={t.status} />
                </li>
              ))}
            </ul>
          )}
        </Section>
      </div>
    </SuperAdminLayout>
  )
}

function Stat({ label, value, hint }: { label: string; value: number; hint?: string }) {
  return (
    <div className="bg-white border border-stone/20 rounded-lg p-4">
      <div className="text-xs uppercase tracking-wide text-text-muted font-semibold">{label}</div>
      <div className="text-3xl font-bold text-text-primary mt-1">{value.toLocaleString()}</div>
      {hint && <div className="text-xs text-text-muted mt-1">{hint}</div>}
    </div>
  )
}

function Section({ title, link, linkLabel, children }: { title: string; link: string; linkLabel: string; children: React.ReactNode }) {
  return (
    <div className="bg-white border border-stone/20 rounded-lg p-4">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-lg font-semibold text-text-primary">{title}</h2>
        <Link href={link} className="text-sm text-[#ED1C24] hover:underline">{linkLabel}</Link>
      </div>
      {children}
    </div>
  )
}

function Empty({ text }: { text: string }) {
  return <div className="py-6 text-center text-text-muted text-sm">{text}</div>
}

export function StatusPill({ status }: { status: string }) {
  const cls = status === 'active'
    ? 'bg-[#15532E]/10 text-[#15532E]'
    : status === 'suspended'
      ? 'bg-[#ED1C24]/10 text-[#ED1C24]'
      : 'bg-stone/10 text-text-muted'
  return <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${cls}`}>{status}</span>
}
